class GiftCardRecipient extends HTMLElement {
  constructor() {
    super();

    this.elements = {
      recipientName: this.querySelector('#recipient-name'),
      recipientEmail: this.querySelector('#recipient-email'),
      recipientTo: this.querySelector('#recipient-to'),
      message: this.querySelector('#gift-message'),
      sendDate: this.querySelector('#send-date')
    };

    // Find hidden fields inside the product form (for proper Shopify email routing)
    this.formFields = {
      to: document.getElementById('form-properties-_to'),
      from: document.getElementById('form-properties-_from'),
      message: document.getElementById('form-properties-_message'),
      sendOn: document.getElementById('form-properties-_send_on')
    };

    this.init();
  }

  init() {
    // Set minimum date to today
    if (this.elements.sendDate) {
      const today = new Date().toISOString().split('T')[0];
      this.elements.sendDate.setAttribute('min', today);
    }

    // Add validation listeners
    this.addValidationListeners();
  }

  addValidationListeners() {
    // Validate email format
    if (this.elements.recipientEmail) {
      this.elements.recipientEmail.addEventListener('blur', () => {
        this.validateEmail();
      });
    }

    // Validate date is not in the past
    if (this.elements.sendDate) {
      this.elements.sendDate.addEventListener('change', () => {
        this.validateDate();
      });
    }

    // If any recipient field is filled, email becomes required
    Object.values(this.elements).forEach(element => {
      if (element && element !== this.elements.recipientEmail && element !== this.elements.recipientTo) {
        element.addEventListener('blur', () => {
          this.checkEmailRequirement();
        });
      }
    });
    
    // Sync _to field when recipient email changes (for form fallback)
    if (this.elements.recipientEmail) {
      this.elements.recipientEmail.addEventListener('input', () => {
        this.syncToField();
      });
      
      // Also sync on blur to ensure it's set before form submission
      this.elements.recipientEmail.addEventListener('blur', () => {
        this.syncToField();
      });
    }

    // Sync other fields when they change
    if (this.elements.recipientName) {
      this.elements.recipientName.addEventListener('input', () => {
        this.syncFromField();
      });
      this.elements.recipientName.addEventListener('blur', () => {
        this.syncFromField();
      });
    }

    if (this.elements.message) {
      this.elements.message.addEventListener('input', () => {
        this.syncMessageField();
      });
      this.elements.message.addEventListener('blur', () => {
        this.syncMessageField();
      });
    }

    if (this.elements.sendDate) {
      this.elements.sendDate.addEventListener('change', () => {
        this.syncSendOnField();
      });
    }
  }

  validateEmail() {
    const email = this.elements.recipientEmail.value.trim();
    if (email && !this.isValidEmail(email)) {
      this.showError(this.elements.recipientEmail, 'Please enter a valid email address');
      return false;
    }
    this.clearError(this.elements.recipientEmail);
    return true;
  }

  validateDate() {
    const dateValue = this.elements.sendDate.value;
    if (!dateValue) return true;

    const selectedDate = new Date(dateValue);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      this.showError(this.elements.sendDate, 'Send date must be today or in the future');
      return false;
    }
    this.clearError(this.elements.sendDate);
    return true;
  }

  checkEmailRequirement() {
    const hasRecipientInfo = 
      (this.elements.recipientName && this.elements.recipientName.value.trim()) ||
      (this.elements.message && this.elements.message.value.trim()) ||
      (this.elements.sendDate && this.elements.sendDate.value);

    if (hasRecipientInfo && this.elements.recipientEmail) {
      const email = this.elements.recipientEmail.value.trim();
      if (!email) {
        this.elements.recipientEmail.setAttribute('required', 'required');
        this.showError(this.elements.recipientEmail, 'Email is required when sending as a gift');
      } else {
        this.elements.recipientEmail.removeAttribute('required');
        this.clearError(this.elements.recipientEmail);
      }
    }
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Sync methods to update hidden fields inside the product form
  syncToField() {
    const email = this.elements.recipientEmail?.value.trim() || '';
    // Sync to section's hidden field
    if (this.elements.recipientTo) {
      this.elements.recipientTo.value = email;
    }
    // Sync to form's hidden field
    if (this.formFields.to) {
      this.formFields.to.value = email;
      console.log('Synced _to field in product form:', email);
    }
  }

  syncFromField() {
    const name = this.elements.recipientName?.value.trim() || '';
    if (this.formFields.from) {
      this.formFields.from.value = name;
    }
  }

  syncMessageField() {
    const message = this.elements.message?.value.trim() || '';
    if (this.formFields.message) {
      this.formFields.message.value = message;
    }
  }

  syncSendOnField() {
    const sendDate = this.elements.sendDate?.value || '';
    if (this.formFields.sendOn) {
      this.formFields.sendOn.value = sendDate;
    }
  }

  // Sync all fields at once (called before form submission)
  syncAllFormFields() {
    this.syncToField();
    this.syncFromField();
    this.syncMessageField();
    this.syncSendOnField();
  }

  showError(element, message) {
    this.clearError(element);
    element.classList.add('error');
    const errorElement = document.createElement('small');
    errorElement.className = 'error-message color-3 text--1';
    errorElement.textContent = message;
    element.parentElement.appendChild(errorElement);
  }

  clearError(element) {
    element.classList.remove('error');
    const errorMessage = element.parentElement.querySelector('.error-message');
    if (errorMessage) {
      errorMessage.remove();
    }
  }

  getProperties() {
    // Sync all form fields before getting properties
    this.syncAllFormFields();

    const properties = {};

    // Add both _from (Shopify special property for sender name) and Recipient Name (for display)
    if (this.elements.recipientName && this.elements.recipientName.value.trim()) {
      const senderName = this.elements.recipientName.value.trim();
      properties['_from'] = senderName; // Shopify special property for sender name in email
      properties['Recipient Name'] = senderName; // For cart display
    }

    // Add both _to (for Shopify email routing) and Recipient Email (for display)
    if (this.elements.recipientEmail && this.elements.recipientEmail.value.trim()) {
      const recipientEmail = this.elements.recipientEmail.value.trim();
      properties['_to'] = recipientEmail; // Shopify special property for email routing
      properties['Recipient Email'] = recipientEmail; // For cart display
      
      // Debug logging to verify _to is being set
      console.log('Gift card recipient email set:', recipientEmail);
      console.log('_to property will be:', properties['_to']);
    }

    // Add both _message (Shopify special property for message) and Message (for display)
    if (this.elements.message && this.elements.message.value.trim()) {
      const message = this.elements.message.value.trim();
      properties['_message'] = message; // Shopify special property for message in email
      properties['Message'] = message; // For cart display
    }

    // Add both _send_on (Shopify special property for send date) and Send On (for display)
    if (this.elements.sendDate && this.elements.sendDate.value) {
      const sendDate = this.elements.sendDate.value;
      properties['_send_on'] = sendDate; // Shopify special property for scheduled send date
      properties['Send On'] = sendDate; // For cart display
    }

    // Debug logging to verify all properties
    if (Object.keys(properties).length > 0) {
      console.log('Gift card properties being set:', properties);
    }

    return properties;
  }

  validate() {
    let isValid = true;

    // Validate email if provided
    if (this.elements.recipientEmail && this.elements.recipientEmail.value.trim()) {
      if (!this.validateEmail()) {
        isValid = false;
      }
    }

    // Validate date if provided
    if (this.elements.sendDate && this.elements.sendDate.value) {
      if (!this.validateDate()) {
        isValid = false;
      }
    }

    // Check if email is required
    this.checkEmailRequirement();
    if (this.elements.recipientEmail && this.elements.recipientEmail.hasAttribute('required') && !this.elements.recipientEmail.value.trim()) {
      isValid = false;
    }

    return isValid;
  }
}

customElements.define('gift-card-recipient', GiftCardRecipient);